<script setup lang="ts">
import { computed, onMounted } from 'vue';

import Image from '@/shared/components/UI/Image.vue';
import StockIndicator from '@/shared/components/UI/StockIndicator.vue';
import { Variant } from '@/shared/composables/patternProducts/patternProducts.types';

import YarnProduct from './VariantSelectorYarnProduct.vue';

defineEmits(['click']);

const props = defineProps<{
  options: Variant[];
  active: Variant | null;
  productType: string;
}>();

onMounted(() =>
  document.querySelector<HTMLDivElement>('[role="option"].active')?.focus()
);

const imgSrc = (src: string, w: number, h: number) => {
  const url = new URL(src);
  url.searchParams.set('width', w.toString());
  url.searchParams.set('height', h.toString());
  url.searchParams.set('q', '80');
  return url.toString();
};

const yarnProductsInStock = computed(() =>
  props.options.filter((variant) => variant.quantityAvailable > 0)
);

const yarnProductsOutOfStock = computed(() =>
  props.options.filter((variant) => variant.quantityAvailable <= 0)
);
</script>

<template>
  <template v-if="productType === 'yarn'">
    <div
      class="isolate ml-px mt-px grid grid-cols-3 max-xs:grid-cols-3 md:grid-cols-6 md:px-5"
    >
      <YarnProduct
        v-for="variant in yarnProductsInStock"
        :key="variant.id"
        :variant="variant"
        :active="active"
        @click="$emit('click', variant)"
      />
    </div>
    <div v-if="yarnProductsOutOfStock.length > 0">
      <div
        class="isolate ml-px mt-px grid grid-cols-3 max-xs:grid-cols-3 md:grid-cols-6 md:px-5"
      >
        <YarnProduct
          v-for="variant in yarnProductsOutOfStock"
          :key="variant.id"
          :variant="variant"
          :active="active"
          @click="$emit('click', variant)"
        />
      </div>
    </div>
  </template>
  <template v-else>
    <div
      v-for="variant in options"
      :key="variant.id"
      tabindex="-1"
      role="option"
      class="flex cursor-pointer items-center gap-2 border-b px-4 py-2 text-sm transition-colors duration-200 first:border-t last:border-b-0 hover:bg-primary/10"
      :class="{
        'active font-bold': active?.id == variant.id
      }"
      @click="$emit('click', variant)"
    >
      <Image
        v-if="variant.image?.url"
        :src="imgSrc(variant.image.url, 60, 60)"
        img-classes="w-14"
        :lazy-load="true"
        loader="fade"
        :width="60"
        :height="60"
        :alt="variant.title"
        class="w-14 overflow-hidden rounded-md border bg-gray-50"
      />

      <div class="flex flex-col gap-0.5">
        <span
          :class="
            variant.quantityAvailable <= 0
              ? 'text-gray-400 line-through'
              : 'text-black'
          "
          >{{ variant.title }}</span
        >
        <StockIndicator class="text-xs" :in-stock="variant.quantityAvailable" />
      </div>
    </div>
  </template>
</template>
